import { useEffect, useRef, useState, createContext, useContext, useCallback } from "react";
import axios from "axios";
import {
    BrowserRouter,
    Routes,
    Route,
    Link,
    Navigate,
    useNavigate,
} from "react-router-dom";
import "@/App.css";
import {
    Coffee,
    Leaf,
    Truck,
    ShieldCheck,
    Phone,
    MessageCircle,
    Mail,
    MapPin,
    Clock,
    Sparkles,
    Mountain,
    ArrowRight,
    Download,
    Quote,
    Star,
    Smartphone,
    CreditCard,
    Building2,
    Wallet,
    Lock,
} from "lucide-react";
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from "@/components/ui/accordion";

const WHATSAPP_NUM = "918248327036";
const PHONE_DISPLAY = "+91 8248327036";
const PHONE_TEL = "+918248327036";
const EMAIL = "idhayamhealth@gmail.com";
const RAZORPAY_URL = "https://razorpay.me/@parthasarathyanand";
const GOOGLE_REVIEW_URL =
    "https://www.google.com/search?q=Yercaud+Vinayaka+Coffee+and+Spices+Yercaud+reviews";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent(
    "Hi Dhaya Coffee, I'd like to order your Yercaud coffee & spices. Please share the price list & a secure payment link.",
)}`;

const IMG = {
    hills: "https://images.unsplash.com/photo-1633437805600-2c58bf56663c?w=1600&auto=format&fit=crop&q=80",
    filter: "https://images.unsplash.com/photo-1758387941825-a6ecaec9c14d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxzb3V0aCUyMGluZGlhbiUyMHRyYWRpdGlvbmFsJTIwZmlsdGVyJTIwY29mZmVlfGVufDB8fHx8MTc4Mjc0MjUxNHww&ixlib=rb-4.1.0&q=85",
    pour: "/images/vinayaka-brewing.webp",
    beans: "https://images.unsplash.com/photo-1447933601403-0c6688de566e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxjb2ZmZWUlMjBiZWFucyUyMHJvYXN0ZWQlMjBwcmVtaXVtfGVufDB8fHx8MTc4MzQ2NTg4OXww&ixlib=rb-4.1.0&q=85",
    spices: "https://images.pexels.com/photos/5669187/pexels-photo-5669187.jpeg",
    mug: "https://images.unsplash.com/photo-1595574444306-e235cef5ea37?w=1400&auto=format&fit=crop&q=80",
    posterLandscape: "/images/vinayaka-landscape.png",
    posterSquare: "/images/vinayaka-square.png",
    brewing: "/images/vinayaka-brewing.webp",
};

/* --------------------------------- NAV ---------------------------------- */
const Nav = () => (
    <nav
        className="glass-nav fixed top-0 inset-x-0 z-50"
        data-testid="site-nav"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
            <a
                href="#top"
                className="flex items-center gap-2"
                data-testid="nav-brand"
            >
                <Coffee
                    className="w-5 h-5 text-[#C84B31]"
                    aria-hidden="true"
                />
                <span className="font-display text-xl text-[#2C1A12] tracking-tight">
                    Yercaud Vinayaka
                </span>
                <span className="text-[#5C4D45] text-xs uppercase tracking-[0.24em] hidden xl:inline">
                    · Dhaya Coffee
                </span>
            </a>
            <div className="hidden md:flex items-center gap-8 text-sm text-[#2C1A12]/80 font-medium">
                <a
                    href="#about"
                    className="hover:text-[#C84B31] transition-colors"
                    data-testid="nav-about"
                >
                    About
                </a>
                <a
                    href="#gallery"
                    className="hover:text-[#C84B31] transition-colors"
                    data-testid="nav-gallery"
                >
                    Gallery
                </a>
                <a
                    href="#coffee"
                    className="hover:text-[#C84B31] transition-colors"
                    data-testid="nav-coffee"
                >
                    Coffee
                </a>
                <a
                    href="#spices"
                    className="hover:text-[#C84B31] transition-colors"
                    data-testid="nav-spices"
                >
                    Spices
                </a>
                <a
                    href="#naturals"
                    className="hover:text-[#C84B31] transition-colors"
                    data-testid="nav-naturals"
                >
                    Honey &amp; Pepper
                </a>
                <a
                    href="#pricing"
                    className="hover:text-[#C84B31] transition-colors"
                    data-testid="nav-pricing"
                >
                    Prices
                </a>
                <a
                    href="#faq"
                    className="hover:text-[#C84B31] transition-colors"
                    data-testid="nav-faq"
                >
                    FAQ
                </a>
                <a
                    href="#feedback"
                    className="hover:text-[#C84B31] transition-colors"
                    data-testid="nav-feedback"
                >
                    Feedback
                </a>
            </div>
            <div className="flex items-center gap-2">
                <a
                    href={RAZORPAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#2C1A12] hover:text-[#C84B31] transition-colors px-3 py-2"
                    data-testid="nav-pay-btn"
                >
                    <Lock className="w-4 h-4" aria-hidden="true" />
                    Pay
                </a>
                <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary text-sm"
                    data-testid="nav-whatsapp-btn"
                >
                    <MessageCircle className="w-4 h-4" aria-hidden="true" /> Order
                    on WhatsApp
                </a>
            </div>
        </div>
    </nav>
);

/* --------------------------------- HERO --------------------------------- */
const Hero = () => (
    <section
        id="top"
        className="relative min-h-[100svh] flex items-end overflow-hidden"
        data-testid="hero-section"
    >
        <img
            src={IMG.hills}
            alt="Yercaud misty coffee estate hills"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2C1A12]/70 via-[#2C1A12]/40 to-[#2C1A12]/90" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40 w-full">
            <div className="max-w-3xl">
                <p
                    className="overline text-[#F5D6C8] mb-6"
                    data-testid="hero-overline"
                >
                    Estate Fresh · Yercaud, Tamil Nadu
                </p>
                <h1
                    className="font-display text-[#F5F2EA] text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.02] tracking-tighter"
                    data-testid="hero-h1"
                >
                    Coffee that still <em className="font-light italic">smells</em> like the hills.
                </h1>
                <p
                    className="mt-8 text-lg md:text-xl text-[#F5F2EA]/85 leading-relaxed max-w-2xl font-body"
                    data-testid="hero-subhead"
                >
                    Small-batch roasted Yercaud coffee powder and estate-fresh
                    spices — freshly ground, hygienically packed, and shipped
                    across India by <strong>Dhaya Coffee</strong>.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                    <a
                        href="/order"
                        className="btn-primary"
                        data-testid="hero-order-cta"
                    >
                        <Coffee className="w-5 h-5" aria-hidden="true" />
                        Buy Coffee Online
                    </a>
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline btn-outline-light"
                        data-testid="hero-whatsapp-cta"
                    >
                        <MessageCircle className="w-5 h-5" aria-hidden="true" />
                        Order on WhatsApp
                    </a>
                    <a
                        href={`tel:${PHONE_TEL}`}
                        className="btn-outline btn-outline-light"
                        data-testid="hero-call-cta"
                    >
                        <Phone className="w-5 h-5" aria-hidden="true" />
                        Call {PHONE_DISPLAY}
                    </a>
                    <a
                        href="/dhaya-price-list.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline btn-outline-light"
                        data-testid="hero-pdf-cta"
                    >
                        <Download className="w-5 h-5" aria-hidden="true" />
                        Price List (PDF)
                    </a>
                </div>
                <div className="mt-14 flex flex-wrap gap-x-10 gap-y-3 text-sm text-[#F5F2EA]/70 font-body">
                    <span className="flex items-center gap-2">
                        <Sparkles
                            className="w-4 h-4 text-[#F5D6C8]"
                            aria-hidden="true"
                        />
                        Freshly roasted & ground
                    </span>
                    <span className="flex items-center gap-2">
                        <Leaf
                            className="w-4 h-4 text-[#F5D6C8]"
                            aria-hidden="true"
                        />
                        Estate-direct sourcing
                    </span>
                    <span className="flex items-center gap-2">
                        <Truck
                            className="w-4 h-4 text-[#F5D6C8]"
                            aria-hidden="true"
                        />
                        Fast pan-India shipping
                    </span>
                </div>
            </div>
        </div>
    </section>
);

/* --------------------------------- ABOUT -------------------------------- */
const About = () => (
    <section
        id="about"
        className="py-24 md:py-32 bg-[#F5F2EA]"
        data-testid="about-section"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-5 relative">
                <img
                    src={IMG.pour}
                    alt="Vinayaka Coffee — Yercaud filter coffee being freshly brewed with our estate ground beans"
                    className="rounded-2xl w-full h-[520px] object-cover shadow-[0_24px_64px_rgba(44,26,18,0.18)]"
                    loading="lazy"
                />
                <div className="hidden md:block absolute -bottom-8 -right-8 bg-[#2C1A12] text-[#F5F2EA] p-6 rounded-2xl max-w-[220px] shadow-[0_18px_48px_rgba(44,26,18,0.25)] drift">
                    <p className="font-display text-4xl font-black leading-none">
                        1,500m
                    </p>
                    <p className="text-xs uppercase tracking-[0.2em] mt-2 opacity-70">
                        Shevaroy Hills altitude
                    </p>
                </div>
            </div>
            <div className="lg:col-span-7">
                <p className="overline mb-5">About Us</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                    A family, an estate, and a promise you can taste.
                </h2>
                <div className="mt-8 space-y-6 text-[#5C4D45] text-lg leading-relaxed font-body">
                    <p>
                        Yercaud Vinayaka Coffee and Spices is a family-run
                        business rooted in the misty hills of Yercaud, Tamil
                        Nadu. Our flagship label,{" "}
                        <strong className="text-[#2C1A12]">Dhaya Coffee</strong>,
                        was born from decades of working with local estates
                        and understanding what a truly good cup of coffee
                        feels like on a rainy Yercaud morning.
                    </p>
                    <p>
                        Everything we sell is either grown on our own land or
                        sourced directly from farmers we know by name. We
                        roast in small batches, grind on demand, and
                        hygienically pack every order — so nothing is ever
                        &ldquo;old stock&rdquo;. That&rsquo;s how we&rsquo;ve earned the trust of
                        home users, cafés, retailers, and gift buyers across
                        India.
                    </p>
                    <p>
                        Alongside our signature coffee, we now offer{" "}
                        <strong className="text-[#2C1A12]">
                            Pure Yercaud Honey
                        </strong>{" "}
                        — raw forest honey collected by the Irula tribe
                        community — and{" "}
                        <strong className="text-[#2C1A12]">
                            Premium Estate Black Pepper
                        </strong>{" "}
                        grown right on our Yercaud hills. Same estate, same
                        promise: natural, hygienic, and honest.
                    </p>
                </div>
            </div>
        </div>
    </section>
);


/* ------------------------------ MISSION QUOTE --------------------------- */
const Mission = () => (
    <section
        className="relative py-24 md:py-36 bg-[#2C1A12] text-[#F5F2EA] overflow-hidden"
        data-testid="mission-section"
        aria-labelledby="mission-quote"
    >
        {/* Decorative background image with heavy overlay */}
        <img
            src={IMG.mug}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-15"
            loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2C1A12] via-[#2C1A12]/85 to-[#2C1A12]" />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
            <Quote
                className="w-12 h-12 md:w-16 md:h-16 text-[#C84B31] mx-auto mb-8 opacity-90"
                aria-hidden="true"
            />
            <blockquote
                id="mission-quote"
                className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light italic tracking-tight leading-[1.15] text-[#F5F2EA]"
            >
                &ldquo;Passionate about bringing the rich taste and heritage of{" "}
                <span className="text-[#F5D6C8] not-italic font-medium">
                    Yercaud coffee
                </span>{" "}
                directly from the hills to customers across India through{" "}
                <span className="text-[#F5D6C8] not-italic font-medium">
                    Vinayaka Coffee Yercaud
                </span>
                .&rdquo;
            </blockquote>

            <div className="mt-12 flex items-center justify-center gap-4">
                <span className="h-px w-12 bg-[#F5D6C8]/40" aria-hidden="true" />
                <p className="overline text-[#F5D6C8]">
                    Our Mission · Dhaya Coffee Family
                </p>
                <span className="h-px w-12 bg-[#F5D6C8]/40" aria-hidden="true" />
            </div>
        </div>
    </section>
);

/* ------------------------------ BRAND GALLERY --------------------------- */
const BrandGallery = () => (
    <section
        id="gallery"
        className="py-24 md:py-32 bg-[#EFE9DC] border-y border-[#2C1A12]/10 overflow-hidden"
        data-testid="gallery-section"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-14">
                <p className="overline mb-5">The Vinayaka Story</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                    Straight from the estate — in every cup.
                </h2>
                <p className="mt-6 text-[#5C4D45] text-lg leading-relaxed font-body">
                    A few frames from the misty hills of Yercaud, our small
                    roastery, and the brass filter that&rsquo;s poured a
                    thousand mornings.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Landscape poster - wide left */}
                <figure
                    className="lift lg:col-span-8 rounded-2xl overflow-hidden bg-[#2C1A12] relative group"
                    data-testid="gallery-poster-landscape"
                >
                    <img
                        src={IMG.posterLandscape}
                        alt="Vinayaka Coffee Yercaud — from the misty hills to your cup, pure tradition in every sip"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        loading="lazy"
                    />
                </figure>

                {/* Brewing image - tall right */}
                <figure
                    className="lift lg:col-span-4 rounded-2xl overflow-hidden bg-[#2C1A12] relative group min-h-[360px]"
                    data-testid="gallery-brewing"
                >
                    <img
                        src={IMG.brewing}
                        alt="Grinding fresh Vinayaka Coffee beans and pour-over brewing — Yercaud estate craft"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        loading="lazy"
                    />
                    <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#2C1A12] via-[#2C1A12]/70 to-transparent p-6 pt-16">
                        <p className="overline text-[#F5D6C8] mb-2">
                            Grind &amp; Brew
                        </p>
                        <p className="font-display text-xl text-[#F5F2EA] tracking-tight leading-tight">
                            Roasted small. Ground fresh. Brewed with love.
                        </p>
                    </figcaption>
                </figure>

                {/* Square poster - wide bottom */}
                <figure
                    className="lift lg:col-span-12 rounded-2xl overflow-hidden bg-[#2C1A12] relative group"
                    data-testid="gallery-poster-square"
                >
                    <img
                        src={IMG.posterSquare}
                        alt="Where the mist meets the mountain — the finest Vinayaka Coffee begins in Yercaud"
                        className="w-full max-h-[720px] object-cover object-center group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                        loading="lazy"
                    />
                </figure>
            </div>
        </div>
    </section>
);


/* ------------------------------ WHY CHOOSE US --------------------------- */
const reasons = [
    {
        icon: Sparkles,
        title: "Small-Batch Roasting",
        body: "Every roast is tasted before it's packed. No shortcuts, no shelf-time — just aroma.",
    },
    {
        icon: Leaf,
        title: "Estate-Direct Sourcing",
        body: "From Yercaud farms we personally know, straight to your kitchen. No long middle chains.",
    },
    {
        icon: ShieldCheck,
        title: "Hygienic, Aroma-Lock Packing",
        body: "Food-safe pouches preserve flavour, freshness and purity through every kilometre.",
    },
    {
        icon: Truck,
        title: "Fast Pan-India Shipping",
        body: "Chennai to Delhi, Bengaluru to Kolkata — dispatched in 24–48 hours, safely delivered.",
    },
];

const WhyChoose = () => (
    <section
        className="py-24 md:py-32 bg-[#EFE9DC] border-y border-[#2C1A12]/10"
        data-testid="why-choose-section"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-2xl">
                <p className="overline mb-5">Why Choose Us</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                    The best coffee powder in Tamil Nadu, on your terms.
                </h2>
            </div>
            <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {reasons.map((r, i) => (
                    <div
                        key={r.title}
                        className="lift bg-[#F5F2EA] border border-[#2C1A12]/10 rounded-2xl p-8"
                        data-testid={`why-card-${i}`}
                    >
                        <div className="w-12 h-12 rounded-full bg-[#C84B31]/10 flex items-center justify-center mb-6">
                            <r.icon
                                className="w-6 h-6 text-[#C84B31]"
                                aria-hidden="true"
                            />
                        </div>
                        <h3 className="font-display text-2xl font-bold text-[#2C1A12] tracking-tight mb-3">
                            {r.title}
                        </h3>
                        <p className="text-[#5C4D45] leading-relaxed font-body text-[15px]">
                            {r.body}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

/* -------------------------- COFFEE COLLECTION --------------------------- */
const Coffees = () => (
    <section
        id="coffee"
        className="py-24 md:py-32 bg-[#F5F2EA]"
        data-testid="coffee-section"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
                <div className="max-w-2xl">
                    <p className="overline mb-5">Our Coffee Collection</p>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                        Made for people who taste with attention.
                    </h2>
                </div>
                <p className="text-[#5C4D45] text-lg max-w-md leading-relaxed font-body">
                    From classic South Indian filter coffee to rare, single-origin
                    Peaberry — every batch is roasted in Yercaud and ground close
                    to dispatch.
                </p>
            </div>

            {/* Bento grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Filter Coffee - big */}
                <article
                    className="lift lg:col-span-7 rounded-2xl overflow-hidden bg-[#2C1A12] text-[#F5F2EA] relative min-h-[420px]"
                    data-testid="coffee-card-filter"
                >
                    <img
                        src={IMG.filter}
                        alt="Traditional South Indian filter coffee powder"
                        className="absolute inset-0 w-full h-full object-cover opacity-70"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C1A12] via-[#2C1A12]/60 to-transparent" />
                    <div className="relative p-8 md:p-10 h-full flex flex-col justify-end min-h-[420px]">
                        <p className="overline text-[#F5D6C8] mb-4">
                            Signature
                        </p>
                        <h3 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-[1.05]">
                            Filter Coffee Powder
                        </h3>
                        <p className="mt-4 text-[#F5F2EA]/85 leading-relaxed max-w-lg font-body">
                            The soul of South Indian mornings. Strong, aromatic
                            decoction with a dark-chocolate finish — the way
                            filter coffee is meant to taste.
                        </p>
                    </div>
                </article>

                {/* Arabica */}
                <article
                    className="lift lg:col-span-5 rounded-2xl overflow-hidden bg-[#F5F2EA] border border-[#2C1A12]/10 flex flex-col"
                    data-testid="coffee-card-arabica"
                >
                    <img
                        src={IMG.beans}
                        alt="Premium roasted Arabica coffee beans"
                        className="w-full h-56 object-cover"
                        loading="lazy"
                    />
                    <div className="p-8">
                        <p className="overline mb-3">Single Origin</p>
                        <h3 className="font-display text-3xl font-black text-[#2C1A12] tracking-tighter">
                            Pure Arabica Coffee
                        </h3>
                        <p className="mt-3 text-[#5C4D45] leading-relaxed font-body text-[15px]">
                            Shade-grown Yercaud Arabica with sweet, floral notes
                            and a clean finish. Perfect for pour-overs and
                            premium espresso.
                        </p>
                    </div>
                </article>

                {/* Peaberry */}
                <article
                    className="lift lg:col-span-5 rounded-2xl overflow-hidden bg-[#4A5D23] text-[#F5F2EA] p-8 md:p-10 flex flex-col justify-between min-h-[320px]"
                    data-testid="coffee-card-peaberry"
                >
                    <div>
                        <p className="overline text-[#EFE9DC] mb-3">Rare</p>
                        <h3 className="font-display text-3xl md:text-4xl font-black tracking-tighter leading-[1.05]">
                            Peaberry Coffee
                        </h3>
                    </div>
                    <p className="mt-6 text-[#F5F2EA]/85 leading-relaxed font-body text-[15px]">
                        A single-bean varietal known for concentrated flavour and
                        syrupy body. A collector&rsquo;s favourite among premium
                        coffee powder lovers.
                    </p>
                </article>

                {/* Freshly Ground */}
                <article
                    className="lift lg:col-span-7 rounded-2xl overflow-hidden bg-[#F5F2EA] border border-[#2C1A12]/10 p-8 md:p-10 flex flex-col justify-between min-h-[320px]"
                    data-testid="coffee-card-freshground"
                >
                    <div>
                        <p className="overline mb-3">Ground To Order</p>
                        <h3 className="font-display text-3xl md:text-4xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                            Freshly Ground Coffee Powder
                        </h3>
                    </div>
                    <p className="mt-6 text-[#5C4D45] leading-relaxed max-w-xl font-body text-[15px]">
                        Ground close to dispatch and sealed instantly. Bright
                        aroma, balanced body — the everyday cup you&rsquo;ll actually
                        look forward to.
                    </p>
                </article>
            </div>
        </div>
    </section>
);

/* -------------------------- SPICES COLLECTION --------------------------- */
const spices = [
    { name: "Pure Pepper", note: "Bold, sharp, full-bodied" },
    { name: "Cardamom", note: "Fragrant green pods from Yercaud" },
    { name: "Cloves", note: "Rich, warm, oil-heavy" },
    { name: "Cinnamon", note: "True bark with a sweet gentle heat" },
    { name: "Nutmeg", note: "Whole nutmeg, creamy & aromatic" },
    { name: "Turmeric", note: "Stone-ground, high-curcumin" },
    { name: "Masala Powders", note: "Biryani, sambar, rasam, chicken & more" },
];

const Spices = () => (
    <section
        id="spices"
        className="py-24 md:py-32 bg-[#2C1A12] text-[#F5F2EA] relative overflow-hidden"
        data-testid="spices-section"
    >
        <div className="absolute inset-y-0 right-0 w-1/2 opacity-30">
            <img
                src={IMG.spices}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover"
                loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#2C1A12] via-[#2C1A12]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-6">
                <p className="overline text-[#F5D6C8] mb-5">
                    Our Spices Collection
                </p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
                    The real taste of the hills, in one honest packet.
                </h2>
                <p className="mt-8 text-[#F5F2EA]/80 text-lg leading-relaxed max-w-xl font-body">
                    Every spice is hand-picked, cleaned, sun-dried and packed
                    hygienically in-house — no cold storage, no long warehouse
                    chains. Fresh spices online, the way they should taste.
                </p>
            </div>
            <div className="lg:col-span-6">
                <ul className="divide-y divide-[#F5F2EA]/15" data-testid="spices-list">
                    {spices.map((s, i) => (
                        <li
                            key={s.name}
                            className="py-5 flex items-baseline justify-between gap-6"
                            data-testid={`spice-item-${i}`}
                        >
                            <span className="font-display text-2xl md:text-3xl font-medium tracking-tight">
                                {s.name}
                            </span>
                            <span className="text-[#F5F2EA]/60 text-sm text-right font-body max-w-[220px]">
                                {s.note}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    </section>
);


/* -------------------- SIGNATURE NATURALS (Honey + Pepper) -------------- */
const honeyBenefits = [
    "100% Pure & Natural — no added sugar",
    "Raw forest honey from Yercaud hills",
    "No preservatives, no heating",
    "Rich aroma & authentic floral taste",
    "Hygienically packed 500g jars",
];

const pepperBenefits = [
    "Estate-grown Yercaud Black Pepper",
    "Bold aroma, sharp, full-bodied heat",
    "Rich in antioxidants & piperine",
    "Freshly cleaned & sun-dried",
    "Premium export-grade quality",
];

const HoneyCard = () => (
    <article
        className="lift bg-white border border-[#2C1A12]/10 rounded-2xl overflow-hidden flex flex-col md:flex-row"
        data-testid="natural-card-honey"
    >
        <div className="md:w-2/5 bg-[#2C1A12] shrink-0">
            <img
                src="/images/raw-honey.jpg"
                alt="Pure Yercaud raw forest honey by the Irula tribe community — 500g jar"
                className="w-full h-full min-h-[280px] object-cover"
                loading="lazy"
            />
        </div>
        <div className="p-8 md:p-10 flex-1 flex flex-col">
            <p className="overline text-[#C84B31] mb-3">Pure Yercaud Honey</p>
            <h3 className="font-display text-3xl md:text-4xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                Raw forest honey. Nothing added.
            </h3>
            <ul className="mt-6 space-y-2 text-[#5C4D45] font-body text-[15px]">
                {honeyBenefits.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                        <ShieldCheck
                            className="w-4 h-4 text-[#4A5D23] mt-1 shrink-0"
                            aria-hidden="true"
                        />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-4">
                <span className="font-display text-3xl font-black text-[#C84B31] tracking-tighter">
                    ₹1,500{" "}
                    <span className="text-sm text-[#5C4D45] font-body font-normal">
                        / 500g
                    </span>
                </span>
                <a
                    href={`${WHATSAPP_URL.split("?")[0]}?text=${encodeURIComponent(
                        "Hi Dhaya Coffee, I'd like to order Pure Yercaud Honey (500g). Please share availability & shipping details.",
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary text-sm"
                    data-testid="honey-order-btn"
                >
                    <MessageCircle className="w-4 h-4" aria-hidden="true" />
                    Order Honey
                </a>
            </div>
        </div>
    </article>
);

const PEPPER_PATTERN_STYLE = {
    backgroundImage:
        "radial-gradient(circle at 30% 30%, #C84B31 2px, transparent 3px), radial-gradient(circle at 70% 60%, #F5D6C8 2px, transparent 3px), radial-gradient(circle at 40% 80%, #C84B31 2px, transparent 3px), radial-gradient(circle at 80% 20%, #F5D6C8 2px, transparent 3px)",
    backgroundSize: "60px 60px",
};

const PepperCard = () => (
    <article
        className="lift bg-[#2C1A12] text-[#F5F2EA] border border-[#2C1A12]/10 rounded-2xl overflow-hidden flex flex-col relative"
        data-testid="natural-card-pepper"
    >
        <div className="p-8 md:p-10 flex-1 flex flex-col relative z-10">
            <p className="overline text-[#F5D6C8] mb-3">Premium Black Pepper</p>
            <h3 className="font-display text-3xl md:text-4xl font-black tracking-tighter leading-[1.05]">
                Bold. Estate-grown. Export-grade.
            </h3>
            <ul className="mt-6 space-y-2 text-[#F5F2EA]/85 font-body text-[15px]">
                {pepperBenefits.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                        <Sparkles
                            className="w-4 h-4 text-[#F5D6C8] mt-1 shrink-0"
                            aria-hidden="true"
                        />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            <div className="mt-auto pt-8 flex flex-wrap items-center gap-4">
                <a
                    href={`${WHATSAPP_URL.split("?")[0]}?text=${encodeURIComponent(
                        "Hi Dhaya Coffee, I'd like to buy Yercaud Estate Black Pepper. Please share sizes, price & shipping details.",
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary text-sm"
                    data-testid="pepper-order-btn"
                >
                    <MessageCircle className="w-4 h-4" aria-hidden="true" />
                    Get Pepper Price
                </a>
                <span className="text-[#F5F2EA]/60 text-xs font-body">
                    Priced by harvest · WhatsApp for latest rate
                </span>
            </div>
        </div>
        <div
            aria-hidden="true"
            className="absolute right-0 top-0 bottom-0 w-1/3 opacity-25"
            style={PEPPER_PATTERN_STYLE}
        />
    </article>
);

const SignatureNaturals = () => (
    <section
        id="naturals"
        className="py-24 md:py-32 bg-[#F5F2EA] border-t border-[#2C1A12]/10"
        data-testid="naturals-section"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-16">
                <p className="overline mb-5">Signature Naturals</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                    Beyond coffee — pure honey & estate black pepper.
                </h2>
                <p className="mt-6 text-[#5C4D45] text-lg leading-relaxed font-body">
                    Two more Yercaud treasures now packed with the same care
                    as our coffee. Raw <strong>forest honey</strong> collected
                    by the Irula tribe, and{" "}
                    <strong>premium estate black pepper</strong> grown on our
                    own hills — shipped fresh across India.
                </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <HoneyCard />
                <PepperCard />
            </div>
        </div>
    </section>
);

/* --------------------------- WHY YERCAUD -------------------------------- */
const WhyYercaud = () => (
    <section
        className="py-24 md:py-32 bg-[#F5F2EA]"
        data-testid="why-yercaud-section"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
                <p className="overline mb-5">Why Yercaud Coffee is Special</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                    1,500 metres above the noise.
                </h2>
                <div className="mt-8 space-y-6 text-[#5C4D45] text-lg leading-relaxed font-body max-w-2xl">
                    <p>
                        Yercaud is one of India&rsquo;s oldest coffee-growing regions,
                        sitting over 1,500 metres high in the Shevaroy Hills.
                        The mist, the cool climate, the shade-tree canopy, and
                        the mineral-rich soil produce coffee cherries that
                        ripen slowly — and slow ripening means deeper flavour.
                    </p>
                    <p>
                        Our Arabica is celebrated for its natural sweetness,
                        mild acidity and lingering finish. Our Peaberry is even
                        rarer — one round bean per cherry, concentrating all
                        that flavour into a single seed. Combined with
                        traditional small-batch roasting, this is what makes
                        <strong className="text-[#2C1A12]">
                            {" "}
                            Dhaya Coffee
                        </strong>{" "}
                        an experience you&rsquo;ll remember cup after cup.
                    </p>
                </div>
                <div className="mt-10 flex flex-wrap gap-8 text-sm">
                    <div className="flex items-center gap-3">
                        <Mountain
                            className="w-5 h-5 text-[#C84B31]"
                            aria-hidden="true"
                        />
                        <span className="text-[#2C1A12] font-medium">
                            Shevaroy Hills, TN
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Leaf
                            className="w-5 h-5 text-[#4A5D23]"
                            aria-hidden="true"
                        />
                        <span className="text-[#2C1A12] font-medium">
                            Shade-grown Arabica
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Coffee
                            className="w-5 h-5 text-[#C84B31]"
                            aria-hidden="true"
                        />
                        <span className="text-[#2C1A12] font-medium">
                            Small-batch roasted
                        </span>
                    </div>
                </div>
            </div>
            <div className="lg:col-span-5">
                <img
                    src={IMG.mug}
                    alt="Freshly brewed Dhaya Coffee in a ceramic mug"
                    className="rounded-2xl w-full h-[560px] object-cover shadow-[0_24px_64px_rgba(44,26,18,0.18)]"
                    loading="lazy"
                />
            </div>
        </div>
    </section>
);


/* -------------------------------- PRICING ------------------------------- */
const priceList = [
    {
        name: "100% Pure Filter Coffee",
        desc: "Premium single-origin, small-batch roasted. No blends, no fillers.",
        price: "1,200",
        unit: "/ kg",
        badge: "Signature",
        image: null,
    },
    {
        name: "Filter Coffee — 80:20 Blend",
        desc: "80% Coffee + 20% Chicory. The classic South Indian decoction.",
        price: "1,000",
        unit: "/ kg",
        badge: "Bestseller",
        image: null,
    },
    {
        name: "Roasted Coffee Seeds",
        desc: "Whole beans, roasted-to-order. Grind fresh at home for peak aroma.",
        price: "1,000",
        unit: "/ kg",
        badge: "Whole Bean",
        image: null,
    },
    {
        name: "Raw Honey — Irula Tribe",
        desc: "Unheated, unfiltered raw honey harvested by the Irula tribe community of Yercaud.",
        price: "1,500",
        unit: "/ 500g",
        badge: "New · Tribal Sourced",
        image: "/images/raw-honey.jpg",
    },
    {
        name: "Coffee + Honey Combo",
        desc: "1kg Filter Coffee + 500g Raw Honey. Save ₹400 vs buying separately.",
        price: "2,100",
        unit: "combo",
        badge: "Best Value",
        image: null,
    },
];

const Pricing = () => (
    <section
        id="pricing"
        className="py-24 md:py-32 bg-[#EFE9DC] border-y border-[#2C1A12]/10"
        data-testid="pricing-section"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
                <div className="max-w-2xl">
                    <p className="overline mb-5">Transparent Pricing</p>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                        No middlemen. No mystery. Just fair, honest prices.
                    </h2>
                </div>
                <a
                    href="/dhaya-price-list.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary self-start lg:self-end"
                    data-testid="pricing-download-pdf"
                >
                    <Download className="w-5 h-5" aria-hidden="true" />
                    Download Price List (PDF)
                </a>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                {priceList.map((p, i) => {
                    const isCombo = p.badge === "Best Value";
                    return (
                    <article
                        key={p.name}
                        className={`lift ${
                            isCombo
                                ? "bg-[#2C1A12] text-[#F5F2EA] border-[#C84B31]"
                                : "bg-[#F5F2EA] text-[#2C1A12] border-[#2C1A12]/10"
                        } border rounded-2xl p-8 flex flex-col overflow-hidden`}
                        data-testid={`price-card-${i}`}
                    >
                        {p.image && (
                            <div className="-mx-8 -mt-8 mb-6 h-52 overflow-hidden bg-[#2C1A12]">
                                <img
                                    src={p.image}
                                    alt={p.name}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        )}
                        <span
                            className={`overline mb-4 ${
                                isCombo ? "text-[#F5D6C8]" : "text-[#4A5D23]"
                            }`}
                        >
                            {p.badge}
                        </span>
                        <h3
                            className={`font-display text-2xl font-bold tracking-tight leading-tight ${
                                isCombo ? "text-[#F5F2EA]" : "text-[#2C1A12]"
                            }`}
                        >
                            {p.name}
                        </h3>
                        <p
                            className={`mt-3 leading-relaxed font-body text-[15px] flex-1 ${
                                isCombo
                                    ? "text-[#F5F2EA]/80"
                                    : "text-[#5C4D45]"
                            }`}
                        >
                            {p.desc}
                        </p>
                        <div className="mt-8 flex items-baseline gap-2 flex-wrap">
                            <span
                                className={`font-display text-4xl font-black tracking-tighter ${
                                    isCombo
                                        ? "text-[#F5D6C8]"
                                        : "text-[#C84B31]"
                                }`}
                            >
                                ₹{p.price}
                            </span>
                            <span
                                className={`font-body text-sm ${
                                    isCombo
                                        ? "text-[#F5F2EA]/70"
                                        : "text-[#5C4D45]"
                                }`}
                            >
                                {p.unit}
                            </span>
                        </div>
                        <a
                            href={`${WHATSAPP_URL.split("?")[0]}?text=${encodeURIComponent(
                                `Hi Dhaya Coffee, I'd like to order: ${p.name}. Please share availability & shipping details.`,
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`mt-6 inline-flex items-center gap-2 font-semibold text-sm hover:gap-3 transition-all ${
                                isCombo
                                    ? "text-[#F5D6C8]"
                                    : "text-[#C84B31]"
                            }`}
                            data-testid={`price-order-${i}`}
                        >
                            Order this on WhatsApp
                            <ArrowRight className="w-4 h-4" aria-hidden="true" />
                        </a>
                    </article>
                    );
                })}
            </div>

            <p className="mt-10 text-sm text-[#5C4D45] font-body">
                Prices as listed above. Spices priced by season — WhatsApp us
                for the latest rates. Shipping charges applied at checkout.
            </p>
        </div>
    </section>
);

/* ------------------------------ TESTIMONIALS ---------------------------- */
const testimonials = [
    {
        quote:
            "Best filter coffee powder I've ordered online. The decoction smells exactly like my grandmother's kitchen in Chennai. Ordered 3 kilos already.",
        name: "Priya R.",
        city: "Bengaluru",
    },
    {
        quote:
            "We serve Dhaya Coffee at our café now. Customers keep asking what changed — it's the freshness. Small-batch roasting is real here.",
        name: "Karthik S.",
        city: "Coimbatore",
    },
    {
        quote:
            "The cardamom and pepper are on a different level. You can smell them the moment you open the pouch. Fast shipping to Mumbai too.",
        name: "Anjali M.",
        city: "Mumbai",
    },
    {
        quote:
            "Bought the Peaberry for a gift and kept it for myself instead. Sweet, syrupy body — genuinely premium coffee powder. Highly recommend.",
        name: "Vikram T.",
        city: "Hyderabad",
    },
];

/* ---------------------- REVIEW CARD (shared) ---------------------------- */
const ReviewCard = ({ quote, name, city, rating = 5, isLive = false, testId }) => (
    <figure
        className="lift bg-[#FFFFFF] border border-[#2C1A12]/10 rounded-2xl p-8 md:p-10 relative"
        data-testid={testId}
    >
        <Quote
            className="absolute top-6 right-6 w-8 h-8 text-[#C84B31]/20"
            aria-hidden="true"
        />
        {isLive && (
            <span className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#4A5D23] bg-[#4A5D23]/10 rounded-full px-2.5 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4A5D23] animate-pulse" />
                Live
            </span>
        )}
        <div
            className={`flex gap-1 mb-4 ${isLive ? "mt-6" : ""}`}
            aria-label={`${rating} star rating`}
        >
            {[0, 1, 2, 3, 4].map((s) => (
                <Star
                    key={s}
                    className={`w-4 h-4 ${
                        s < rating
                            ? "fill-[#C84B31] text-[#C84B31]"
                            : "text-[#2C1A12]/20"
                    }`}
                    aria-hidden="true"
                />
            ))}
        </div>
        <blockquote className="font-display text-xl md:text-2xl text-[#2C1A12] leading-snug tracking-tight">
            &ldquo;{quote}&rdquo;
        </blockquote>
        <figcaption className="mt-6 flex items-center gap-3 font-body">
            <div className="w-10 h-10 rounded-full bg-[#C84B31]/10 text-[#C84B31] flex items-center justify-center font-bold">
                {name.charAt(0).toUpperCase()}
            </div>
            <div>
                <p className="font-semibold text-[#2C1A12] text-sm">{name}</p>
                {city && (
                    <p className="text-[#5C4D45] text-xs">{city}</p>
                )}
            </div>
        </figcaption>
    </figure>
);

const Testimonials = () => {
    const [liveReviews, setLiveReviews] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await axios.get(`${API}/feedback?limit=20`);
                if (!mounted) return;
                const good = (res.data || [])
                    .filter(
                        (r) =>
                            r.rating >= 4 &&
                            typeof r.comment === "string" &&
                            r.comment.trim().length >= 20,
                    )
                    .slice(0, 4);
                setLiveReviews(good);
            } catch (_err) {
                // Silent fallback to static testimonials — non-blocking UX choice
                if (process.env.NODE_ENV !== "production") {
                    // eslint-disable-next-line no-console
                    console.debug("Feedback fetch fell back to static:", _err?.message);
                }
            } finally {
                if (mounted) setLoaded(true);
            }
        })();
        return () => {
            mounted = false;
        };
        // API + axios are module-level; state setters are stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showLive = liveReviews.length > 0;

    return (
        <section
            id="testimonials"
            className="py-24 md:py-32 bg-[#F5F2EA]"
            data-testid="testimonials-section"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="max-w-2xl mb-16">
                    <p className="overline mb-5">
                        {showLive
                            ? "Live from Our Customers"
                            : "Loved Across India"}
                    </p>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]">
                        Real customers. Real cups. Real reviews.
                    </h2>
                    {showLive && (
                        <p className="mt-6 text-[#5C4D45] font-body">
                            Below are the latest 4-star and 5-star reviews
                            submitted by real Dhaya Coffee customers — pulled
                            live from our feedback wall.
                        </p>
                    )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {showLive
                        ? liveReviews.map((r, i) => (
                              <ReviewCard
                                  key={r.id || i}
                                  quote={r.comment}
                                  name={r.name}
                                  city=""
                                  rating={r.rating}
                                  isLive
                                  testId={`live-review-${i}`}
                              />
                          ))
                        : testimonials.map((t, i) => (
                              <ReviewCard
                                  key={t.name}
                                  quote={t.quote}
                                  name={t.name}
                                  city={t.city}
                                  rating={5}
                                  testId={`testimonial-${i}`}
                              />
                          ))}
                </div>

                {loaded && !showLive && (
                    <p className="mt-8 text-xs text-[#5C4D45]/70 font-body italic">
                        Sample reviews shown above · Real customer reviews will
                        appear here as soon as they roll in.
                    </p>
                )}
            </div>
        </section>
    );
};

/* --------------------------------- FAQ ---------------------------------- */
const faqs = [
    {
        q: "How fresh is your coffee powder?",
        a: "Every order is roasted in small batches and ground close to dispatch. You receive your fresh coffee powder online within days of grinding.",
    },
    {
        q: "Do you deliver across India?",
        a: "Yes. We ship pan-India — Tamil Nadu, Karnataka, Kerala, Andhra Pradesh, Maharashtra, Delhi NCR, West Bengal and everywhere in between.",
    },
    {
        q: "Which coffee is best for South Indian filter coffee?",
        a: "Our filter coffee powder — a classic 80:20 or 70:30 coffee-to-chicory blend — is designed exactly for that strong, aromatic decoction.",
    },
    {
        q: "Do you sell 100% pure Arabica and Peaberry?",
        a: "Yes. Our Arabica coffee and Peaberry coffee are 100% single-origin from Yercaud, with no blending and no additives.",
    },
    {
        q: "Are your spices really estate-fresh?",
        a: "Absolutely. Our estate fresh spices are hand-picked, cleaned, sun-dried and packed in-house — no cold storage, no long warehouse chains.",
    },
    {
        q: "Can I place a bulk order for my café or shop?",
        a: "Yes. We supply cafés, retailers and gift stores across India. WhatsApp us at +91 8248327036 for wholesale pricing.",
    },
    {
        q: "How do you pack the products?",
        a: "All items are packed in food-safe, aroma-lock pouches to preserve freshness and hygiene during transit.",
    },
    {
        q: "How long does delivery take?",
        a: "Most orders are dispatched within 24–48 hours. Delivery typically takes 2–6 business days depending on your location.",
    },
    {
        q: "Do you offer gift hampers?",
        a: "Yes. Curated coffee and spice hampers are perfect for festivals, weddings and corporate gifting. Message us for custom hampers.",
    },
    {
        q: "How do I place an order?",
        a: "The easiest way — WhatsApp us on +91 8248327036. You can also call the same number or email idhayamhealth@gmail.com.",
    },
    {
        q: "Is your Yercaud honey 100% pure and natural?",
        a: "Yes. Our Yercaud honey is raw, unheated, unfiltered forest honey — collected by the Irula tribe community. No added sugar, no preservatives, no dilution.",
    },
    {
        q: "What is the shelf life of your honey and how do I store it?",
        a: "Raw honey has a shelf life of 24 months when stored in a cool, dry place away from direct sunlight. Natural crystallisation may occur — that's a sign of purity. Just warm the jar gently in warm water.",
    },
    {
        q: "Is your black pepper freshly harvested?",
        a: "Yes. Our estate black pepper is harvested, cleaned and sun-dried in small lots. We only pack after your order so you get maximum aroma, piperine and flavour.",
    },
    {
        q: "Do you sell whole black pepper or ground pepper?",
        a: "We primarily sell whole black peppercorns (recommended for maximum freshness). Ground pepper is available on request — WhatsApp us for grind preference.",
    },
    {
        q: "How should I store black pepper for best flavour?",
        a: "Store in an airtight jar, away from heat and sunlight. Whole peppercorns stay fresh for up to 2 years; ground pepper is best used within 6 months.",
    },
    {
        q: "Can I place bulk orders of honey or pepper for my business?",
        a: "Absolutely. We supply cafés, restaurants, retailers and gift stores across India. WhatsApp +91 8248327036 for bulk pricing and export enquiries.",
    },
];

/* --------------------------------- FEEDBACK ----------------------------- */
const StarRatingInput = ({ value, onChange }) => (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
            <button
                key={n}
                type="button"
                role="radio"
                aria-checked={value === n}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                onClick={() => onChange(n)}
                className="p-1 focus:outline-none"
                data-testid={`feedback-star-${n}`}
            >
                <Star
                    className={`w-8 h-8 transition-colors ${
                        n <= value
                            ? "fill-[#C84B31] text-[#C84B31]"
                            : "text-[#2C1A12]/25 hover:text-[#C84B31]/60"
                    }`}
                    aria-hidden="true"
                />
            </button>
        ))}
    </div>
);

const Feedback = () => {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status === "submitting") return;
        setStatus("submitting");
        setErrorMsg("");
        try {
            await axios.post(`${API}/feedback`, {
                name: name.trim(),
                phone: phone.trim(),
                rating,
                comment: comment.trim(),
            });
            setStatus("success");
            setName("");
            setPhone("");
            setRating(5);
            setComment("");
        } catch (err) {
            setStatus("error");
            const detail =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            setErrorMsg(
                typeof detail === "string"
                    ? detail
                    : "Something went wrong. Please try again.",
            );
        }
    };

    return (
        <section
            id="feedback"
            className="py-24 md:py-32 bg-[#F5F2EA] border-t border-[#2C1A12]/10"
            data-testid="feedback-section"
            aria-labelledby="feedback-heading"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                {/* Left: heading + Google review CTA */}
                <div className="lg:col-span-5">
                    <p className="overline mb-5">Your Voice Matters</p>
                    <h2
                        id="feedback-heading"
                        className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05]"
                    >
                        Tell us how the cup tasted.
                    </h2>
                    <p className="mt-6 text-[#5C4D45] text-lg leading-relaxed font-body">
                        Loved it? Hated it? Have a suggestion? Your honest
                        feedback helps us roast better, pack fresher, and serve
                        you longer.
                    </p>

                    <div className="mt-10 p-8 rounded-2xl bg-[#2C1A12] text-[#F5F2EA]">
                        <p className="overline text-[#F5D6C8] mb-3">
                            Public Review
                        </p>
                        <h3 className="font-display text-2xl font-black tracking-tight">
                            Help others discover us on Google.
                        </h3>
                        <p className="mt-3 text-[#F5F2EA]/80 text-sm leading-relaxed font-body">
                            A Google review boosts our visibility in Yercaud
                            search results and helps other coffee lovers find
                            us. Takes 20 seconds.
                        </p>
                        <a
                            href={GOOGLE_REVIEW_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 btn-primary text-sm inline-flex"
                            data-testid="google-review-btn"
                        >
                            <Star
                                className="w-4 h-4 fill-current"
                                aria-hidden="true"
                            />
                            Review us on Google
                            <ArrowRight className="w-4 h-4" aria-hidden="true" />
                        </a>
                    </div>
                </div>

                {/* Right: private feedback form */}
                <div className="lg:col-span-7">
                    <form
                        onSubmit={handleSubmit}
                        className="bg-white border border-[#2C1A12]/10 rounded-2xl p-8 md:p-10 shadow-[0_8px_32px_rgba(44,26,18,0.06)]"
                        data-testid="feedback-form"
                        noValidate
                    >
                        <p className="overline mb-5">Private Feedback</p>
                        <h3 className="font-display text-2xl md:text-3xl font-black text-[#2C1A12] tracking-tight mb-8">
                            Send us a note
                        </h3>

                        <div className="grid sm:grid-cols-2 gap-5 mb-5">
                            <label className="block">
                                <span className="text-xs font-semibold uppercase tracking-widest text-[#5C4D45]">
                                    Your Name *
                                </span>
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    maxLength={80}
                                    className="mt-2 w-full rounded-lg border border-[#2C1A12]/20 bg-[#F5F2EA]/50 px-4 py-3 text-[#2C1A12] font-body focus:outline-none focus:border-[#C84B31] focus:ring-2 focus:ring-[#C84B31]/20"
                                    data-testid="feedback-name-input"
                                    placeholder="Priya R."
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold uppercase tracking-widest text-[#5C4D45]">
                                    Phone (optional)
                                </span>
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    maxLength={20}
                                    className="mt-2 w-full rounded-lg border border-[#2C1A12]/20 bg-[#F5F2EA]/50 px-4 py-3 text-[#2C1A12] font-body focus:outline-none focus:border-[#C84B31] focus:ring-2 focus:ring-[#C84B31]/20"
                                    data-testid="feedback-phone-input"
                                    placeholder="+91 98xxxxxxxx"
                                />
                            </label>
                        </div>

                        <div className="mb-5">
                            <span className="text-xs font-semibold uppercase tracking-widest text-[#5C4D45] block mb-3">
                                Your Rating *
                            </span>
                            <StarRatingInput value={rating} onChange={setRating} />
                        </div>

                        <label className="block mb-6">
                            <span className="text-xs font-semibold uppercase tracking-widest text-[#5C4D45]">
                                Your Feedback *
                            </span>
                            <textarea
                                required
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={5}
                                minLength={3}
                                maxLength={1000}
                                className="mt-2 w-full rounded-lg border border-[#2C1A12]/20 bg-[#F5F2EA]/50 px-4 py-3 text-[#2C1A12] font-body focus:outline-none focus:border-[#C84B31] focus:ring-2 focus:ring-[#C84B31]/20 resize-none"
                                data-testid="feedback-comment-input"
                                placeholder="Tell us about your Dhaya Coffee experience…"
                            />
                        </label>

                        {status === "success" && (
                            <div
                                className="mb-5 rounded-lg bg-[#4A5D23]/10 border border-[#4A5D23]/30 p-4 text-[#4A5D23] text-sm font-body"
                                data-testid="feedback-success"
                                role="status"
                            >
                                Thank you! Your feedback has reached us. We
                                truly appreciate it.
                            </div>
                        )}
                        {status === "error" && (
                            <div
                                className="mb-5 rounded-lg bg-[#C84B31]/10 border border-[#C84B31]/30 p-4 text-[#C84B31] text-sm font-body"
                                data-testid="feedback-error"
                                role="alert"
                            >
                                {errorMsg}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={status === "submitting"}
                            className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="feedback-submit-btn"
                        >
                            {status === "submitting" ? (
                                "Sending…"
                            ) : (
                                <>
                                    <MessageCircle
                                        className="w-4 h-4"
                                        aria-hidden="true"
                                    />
                                    Send Feedback
                                    <ArrowRight
                                        className="w-4 h-4"
                                        aria-hidden="true"
                                    />
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
};


const FAQ = () => (
    <section
        id="faq"
        className="py-24 md:py-32 bg-[#EFE9DC] border-t border-[#2C1A12]/10"
        data-testid="faq-section"
    >
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <p className="overline mb-5">Frequently Asked</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#2C1A12] tracking-tighter leading-[1.05] max-w-3xl">
                Everything you might want to ask, before your first cup.
            </h2>
            <div className="mt-14">
                <Accordion
                    type="single"
                    collapsible
                    className="w-full"
                    data-testid="faq-accordion"
                >
                    {faqs.map((f, i) => (
                        <AccordionItem
                            key={f.q}
                            value={`item-${i}`}
                            className="border-b border-[#2C1A12]/15"
                            data-testid={`faq-item-${i}`}
                        >
                            <AccordionTrigger className="text-left py-6 font-display text-lg md:text-xl text-[#2C1A12] hover:no-underline tracking-tight">
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-[#5C4D45] text-base leading-relaxed font-body pb-6 pr-6">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </div>
    </section>
);

/* --------------------------------- CTA ---------------------------------- */
const CTA = () => (
    <section
        className="py-24 md:py-32 bg-[#C84B31] text-[#F5F2EA] relative overflow-hidden"
        data-testid="final-cta-section"
    >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 relative">
            <p className="overline text-[#F5D6C8] mb-6">
                Ready when you are
            </p>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] max-w-4xl">
                Taste the real Yercaud — one cup, one spoon at a time.
            </h2>
            <p className="mt-8 text-[#F5F2EA]/90 text-lg md:text-xl leading-relaxed max-w-2xl font-body">
                Message us the word <strong>&ldquo;COFFEE&rdquo;</strong> or{" "}
                <strong>&ldquo;SPICES&rdquo;</strong> on WhatsApp and we&rsquo;ll send you the
                latest price list within minutes.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
                <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary !bg-[#2C1A12] hover:!bg-black"
                    data-testid="cta-whatsapp"
                    style={{ boxShadow: "0 12px 32px rgba(44,26,18,0.4)" }}
                >
                    <MessageCircle className="w-5 h-5" aria-hidden="true" />
                    WhatsApp Us Now
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </a>
                <a
                    href={`tel:${PHONE_TEL}`}
                    className="btn-outline btn-outline-light"
                    data-testid="cta-call"
                >
                    <Phone className="w-5 h-5" aria-hidden="true" />
                    Call {PHONE_DISPLAY}
                </a>
                <a
                    href={`mailto:${EMAIL}`}
                    className="btn-outline btn-outline-light"
                    data-testid="cta-email"
                >
                    <Mail className="w-5 h-5" aria-hidden="true" />
                    Email Us
                </a>
            </div>
        </div>
    </section>
);


/* --------------------------- PAYMENT OPTIONS ---------------------------- */
const paymentMethods = [
    {
        icon: Smartphone,
        title: "UPI",
        note: "Google Pay · PhonePe · Paytm · BHIM",
    },
    {
        icon: CreditCard,
        title: "Cards",
        note: "Visa · MasterCard · RuPay · Amex",
    },
    {
        icon: Building2,
        title: "Net Banking",
        note: "All major Indian banks",
    },
    {
        icon: Wallet,
        title: "Wallets",
        note: "Paytm · Mobikwik · Freecharge",
    },
];

const PaymentOptions = () => (
    <section
        className="py-16 md:py-20 bg-[#F5F2EA] border-t border-[#2C1A12]/10"
        data-testid="payment-options-section"
        aria-labelledby="payment-heading"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 mb-10">
                <div>
                    <p className="overline mb-4">Secure Payments</p>
                    <h2
                        id="payment-heading"
                        className="font-display text-3xl md:text-4xl font-black text-[#2C1A12] tracking-tighter leading-tight"
                    >
                        Pay any way you like — safe &amp; simple.
                    </h2>
                </div>
                <div className="flex items-center gap-3 text-[#5C4D45] text-sm font-body">
                    <Lock
                        className="w-4 h-4 text-[#4A5D23]"
                        aria-hidden="true"
                    />
                    <span>
                        Powered by Razorpay · 256-bit SSL encrypted checkout
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {paymentMethods.map((m) => (
                    <div
                        key={m.title}
                        className="lift bg-white border border-[#2C1A12]/10 rounded-2xl p-6"
                        data-testid={`pay-${m.title.toLowerCase().replace(/\s/g, "-")}`}
                    >
                        <div className="w-10 h-10 rounded-full bg-[#C84B31]/10 flex items-center justify-center mb-4">
                            <m.icon
                                className="w-5 h-5 text-[#C84B31]"
                                aria-hidden="true"
                            />
                        </div>
                        <p className="font-display text-xl font-bold text-[#2C1A12] tracking-tight">
                            {m.title}
                        </p>
                        <p className="text-[#5C4D45] text-xs mt-1 font-body">
                            {m.note}
                        </p>
                    </div>
                ))}
            </div>

            <p className="mt-8 text-sm text-[#5C4D45] font-body max-w-3xl">
                Ready to pay? Tap <strong className="text-[#2C1A12]">Pay Now</strong>{" "}
                below to open our secure Razorpay page — enter the amount your
                order comes to, and pay via UPI, card, net banking or wallet.
                Send us the payment screenshot on WhatsApp and we ship the same
                day.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
                <a
                    href={RAZORPAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                    data-testid="pay-now-btn"
                >
                    <Lock className="w-5 h-5" aria-hidden="true" />
                    Pay Now via Razorpay
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </a>
                <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline"
                    data-testid="pay-whatsapp-btn"
                >
                    <MessageCircle className="w-5 h-5" aria-hidden="true" />
                    Send Screenshot on WhatsApp
                </a>
            </div>
        </div>
    </section>
);

/* -------------------------------- FOOTER -------------------------------- */
const Footer = () => (
    <footer
        className="bg-[#2C1A12] text-[#F5F2EA] pt-20 pb-10"
        data-testid="site-footer"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-6">
                    <p className="overline text-[#F5D6C8] mb-5">
                        Yercaud, Tamil Nadu · India
                    </p>
                    <h3 className="font-display text-5xl md:text-6xl font-black tracking-tighter leading-[1.02]">
                        Yercaud
                        <br />
                        Vinayaka
                        <span className="text-[#C84B31]">.</span>
                    </h3>
                    <p className="mt-6 text-[#F5F2EA]/70 leading-relaxed max-w-md font-body">
                        Home of <strong>Dhaya Coffee</strong>. Estate-fresh
                        Yercaud coffee powder and authentic spices, roasted
                        small, packed clean, and shipped fast across India.
                    </p>
                </div>
                <div className="lg:col-span-3 space-y-4">
                    <p className="overline text-[#F5D6C8] mb-2">Contact</p>
                    <a
                        href={`tel:${PHONE_TEL}`}
                        className="flex items-center gap-3 text-[#F5F2EA]/85 hover:text-[#F5F2EA] transition-colors"
                        data-testid="footer-phone"
                    >
                        <Phone className="w-4 h-4" aria-hidden="true" />
                        {PHONE_DISPLAY}
                    </a>
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-[#F5F2EA]/85 hover:text-[#F5F2EA] transition-colors"
                        data-testid="footer-whatsapp"
                    >
                        <MessageCircle className="w-4 h-4" aria-hidden="true" />
                        WhatsApp
                    </a>
                    <a
                        href={`mailto:${EMAIL}`}
                        className="flex items-center gap-3 text-[#F5F2EA]/85 hover:text-[#F5F2EA] transition-colors break-all"
                        data-testid="footer-email"
                    >
                        <Mail
                            className="w-4 h-4 shrink-0"
                            aria-hidden="true"
                        />
                        {EMAIL}
                    </a>
                </div>
                <div className="lg:col-span-3 space-y-4">
                    <p className="overline text-[#F5D6C8] mb-2">
                        Address & Hours
                    </p>
                    <p className="flex items-start gap-3 text-[#F5F2EA]/85">
                        <MapPin
                            className="w-4 h-4 mt-1 shrink-0"
                            aria-hidden="true"
                        />
                        <span>
                            Sri Vinayaga Coffee &amp; Spice,
                            <br />
                            CSI Complex, Opp. Yercaud Bus Stand,
                            <br />
                            Yercaud, Tamil Nadu, India
                        </span>
                    </p>
                    <p className="flex items-start gap-3 text-[#F5F2EA]/85">
                        <Clock
                            className="w-4 h-4 mt-1 shrink-0"
                            aria-hidden="true"
                        />
                        <span>
                            Mon – Sat · 8:00 AM – 8:00 PM
                            <br />
                            Sun · 9:00 AM – 2:00 PM
                        </span>
                    </p>
                </div>
            </div>
            <div className="mt-16 pt-8 border-t border-[#F5F2EA]/15 flex flex-col md:flex-row justify-between gap-4 text-sm text-[#F5F2EA]/55 font-body">
                <p>
                    © {new Date().getFullYear()} Yercaud Vinayaka Coffee and
                    Spices · Dhaya Coffee. All rights reserved.
                </p>
                <p>Yercaud coffee powder · Fresh spices online · Made in India</p>
            </div>
        </div>
    </footer>
);

/* --------------------------- FLOATING WHATSAPP -------------------------- */
const WhatsAppFAB = () => (
    <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with Dhaya Coffee on WhatsApp"
        className="fixed z-[100] bottom-5 right-5 md:bottom-8 md:right-8 group"
        data-testid="whatsapp-fab"
    >
        <span
            className="absolute inset-0 rounded-full bg-[#25D366] opacity-70 animate-ping"
            aria-hidden="true"
        />
        <span className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] shadow-[0_10px_30px_rgba(37,211,102,0.45)] group-hover:scale-110 transition-transform">
            <MessageCircle
                className="w-7 h-7 md:w-8 md:h-8 text-white fill-white"
                aria-hidden="true"
            />
        </span>
        <span className="hidden md:block absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap bg-[#2C1A12] text-[#F5F2EA] text-sm font-body px-4 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
            Chat with us
        </span>
    </a>
);

/* -------------------------------- MARKETING ----------------------------- */
function Marketing() {
    const rootRef = useRef(null);

    // Scroll-reveal: one-shot IntersectionObserver setup on mount.
    // `IntersectionObserver`, `e`, `el` are browser globals / local callback
    // variables, NOT effect-closure captures — the empty dep array is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const els = document.querySelectorAll(".reveal");
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("is-in");
                        io.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.12 },
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);

    return (
        <div className="App min-h-screen bg-[#F5F2EA] text-[#2C1A12]" ref={rootRef}>
            <div className="grain-overlay" aria-hidden="true" />
            <Nav />
            <main data-testid="main-content">
                <Hero />
                <About />
                <Mission />
                <BrandGallery />
                <WhyChoose />
                <Coffees />
                <Pricing />
                <Spices />
                <SignatureNaturals />
                <WhyYercaud />
                <Testimonials />
                <Feedback />
                <FAQ />
                <CTA />
            </main>
            <PaymentOptions />
            <Footer />
            <WhatsAppFAB />
        </div>
    );
}

/* ============================== ORDER FLOW =============================== */
const ORDER_STEPS = [
    { key: "quantity", label: "Quantity" },
    { key: "format", label: "Format" },
    { key: "name", label: "Name" },
    { key: "address", label: "Address" },
    { key: "phone", label: "Phone" },
];

const OrderShell = ({ children }) => (
    <div className="min-h-screen bg-[#F5F2EA] text-[#2C1A12] flex flex-col">
        <header className="bg-[#2C1A12] text-[#F5F2EA] py-5 px-6">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="order-brand-back">
                    <Coffee className="w-5 h-5 text-[#F5D6C8]" aria-hidden="true" />
                    <span className="font-display text-xl tracking-tight">
                        Vinayaka Coffee
                    </span>
                </Link>
                <Link
                    to="/"
                    className="text-sm text-[#F5F2EA]/70 hover:text-[#F5F2EA] transition-colors"
                    data-testid="order-back-home"
                >
                    ← Back to Home
                </Link>
            </div>
        </header>
        <main className="flex-1 py-10 md:py-16 px-6">
            <div className="max-w-2xl mx-auto">{children}</div>
        </main>
    </div>
);

const StepProgress = ({ current }) => (
    <ol className="flex items-center gap-2 mb-10" aria-label="Order progress">
        {ORDER_STEPS.map((s, i) => {
            const state = i < current ? "done" : i === current ? "active" : "pending";
            return (
                <li key={s.key} className="flex-1 flex items-center gap-2">
                    <div
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                            state === "pending"
                                ? "bg-[#2C1A12]/15"
                                : "bg-[#C84B31]"
                        }`}
                    />
                </li>
            );
        })}
    </ol>
);

const parseIntlPhone = (s) => s.replace(/[^\d+]/g, "");

function OrderFlow() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [data, setData] = useState({
        quantity_kg: "",
        coffee_format: "",
        customer_name: "",
        address: "",
        phone: "",
    });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [placedOrder, setPlacedOrder] = useState(null);

    const update = (key, value) => setData((d) => ({ ...d, [key]: value }));

    const validateStep = () => {
        setError("");
        if (step === 0) {
            const n = parseFloat(data.quantity_kg);
            if (!n || isNaN(n) || n <= 0)
                return "Please enter a quantity greater than 0 (e.g., 1, 2, 2.5).";
            if (n > 100) return "For orders above 100 kg, please WhatsApp us directly.";
        }
        if (step === 1 && !["powder", "beans"].includes(data.coffee_format))
            return "Please choose Coffee Powder or Roasted Coffee Beans.";
        if (step === 2 && !data.customer_name.trim())
            return "Please tell us your full name.";
        if (step === 3 && data.address.trim().length < 5)
            return "Please enter your complete delivery address.";
        if (step === 4) {
            const p = parseIntlPhone(data.phone);
            if (p.length < 8) return "Please enter a valid phone number (min 8 digits).";
            if (p.length > 15) return "Phone number looks too long — please recheck.";
        }
        return "";
    };

    const goNext = async () => {
        const err = validateStep();
        if (err) { setError(err); return; }
        if (step < ORDER_STEPS.length - 1) {
            setStep(step + 1);
        } else {
            await submit();
        }
    };
    const goPrev = () => { setError(""); setStep(Math.max(0, step - 1)); };

    const submit = async () => {
        setSubmitting(true);
        setError("");
        try {
            const payload = {
                quantity_kg: parseFloat(data.quantity_kg),
                coffee_format: data.coffee_format,
                customer_name: data.customer_name.trim(),
                address: data.address.trim(),
                phone: data.phone.trim(),
            };
            const { data: order } = await axios.post(`${API}/orders`, payload);
            setPlacedOrder(order);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(
                Array.isArray(d)
                    ? d.map((x) => x.msg).join(" · ")
                    : typeof d === "string"
                    ? d
                    : "Could not place order. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (placedOrder) return <OrderSuccess order={placedOrder} onReset={() => navigate("/")} />;

    return (
        <OrderShell>
            <p className="overline mb-4">
                Step {step + 1} of {ORDER_STEPS.length} · {ORDER_STEPS[step].label}
            </p>
            <StepProgress current={step} />

            {step === 0 && (
                <StepCard
                    title="How many kg would you like to order?"
                    hint="You can enter whole or decimal values, e.g., 1, 2, 2.5."
                >
                    <input
                        type="number"
                        step="0.25"
                        min="0.25"
                        inputMode="decimal"
                        value={data.quantity_kg}
                        onChange={(e) => update("quantity_kg", e.target.value)}
                        placeholder="e.g., 1"
                        autoFocus
                        className="w-full text-4xl md:text-5xl font-display font-black tracking-tighter rounded-2xl border-2 border-[#2C1A12]/15 bg-white px-6 py-6 focus:outline-none focus:border-[#C84B31]"
                        data-testid="order-qty-input"
                    />
                    <p className="mt-3 text-[#5C4D45] text-sm">Kilograms</p>
                </StepCard>
            )}

            {step === 1 && (
                <StepCard title="Which format do you prefer?" hint="Pick one below.">
                    <div className="grid sm:grid-cols-2 gap-4">
                        {[
                            {
                                v: "powder",
                                title: "Coffee Powder",
                                desc: "Freshly ground · Ready to brew filter coffee immediately.",
                            },
                            {
                                v: "beans",
                                title: "Roasted Coffee Beans",
                                desc: "Whole beans · Grind fresh at home for peak aroma.",
                            },
                        ].map((opt) => {
                            const active = data.coffee_format === opt.v;
                            return (
                                <button
                                    key={opt.v}
                                    type="button"
                                    onClick={() => update("coffee_format", opt.v)}
                                    className={`text-left rounded-2xl border-2 p-6 md:p-8 transition-all ${
                                        active
                                            ? "border-[#C84B31] bg-[#C84B31]/5 shadow-[0_12px_32px_rgba(200,75,49,0.15)]"
                                            : "border-[#2C1A12]/15 bg-white hover:border-[#C84B31]/40"
                                    }`}
                                    data-testid={`order-format-${opt.v}`}
                                >
                                    <p className="font-display text-2xl font-bold text-[#2C1A12] tracking-tight">
                                        {opt.title}
                                    </p>
                                    <p className="mt-2 text-[#5C4D45] font-body text-sm leading-relaxed">
                                        {opt.desc}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                </StepCard>
            )}

            {step === 2 && (
                <StepCard title="What's your full name?" hint="This helps us prepare your shipment label.">
                    <input
                        type="text"
                        value={data.customer_name}
                        onChange={(e) => update("customer_name", e.target.value)}
                        placeholder="e.g., Parthasarathy Anand"
                        maxLength={100}
                        autoFocus
                        className="w-full text-lg rounded-2xl border-2 border-[#2C1A12]/15 bg-white px-6 py-5 focus:outline-none focus:border-[#C84B31]"
                        data-testid="order-name-input"
                    />
                </StepCard>
            )}

            {step === 3 && (
                <StepCard title="Where should we deliver?" hint="Full address including PIN code, please.">
                    <textarea
                        value={data.address}
                        onChange={(e) => update("address", e.target.value)}
                        placeholder="Flat / House no, Street, City, State, PIN"
                        maxLength={500}
                        rows={5}
                        autoFocus
                        className="w-full text-lg rounded-2xl border-2 border-[#2C1A12]/15 bg-white px-6 py-4 focus:outline-none focus:border-[#C84B31] resize-none"
                        data-testid="order-address-input"
                    />
                </StepCard>
            )}

            {step === 4 && (
                <StepCard title="Your phone number?" hint="We'll only use this to confirm your order.">
                    <input
                        type="tel"
                        value={data.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+91 98xxxxxxxx"
                        maxLength={20}
                        autoFocus
                        className="w-full text-lg rounded-2xl border-2 border-[#2C1A12]/15 bg-white px-6 py-5 focus:outline-none focus:border-[#C84B31]"
                        data-testid="order-phone-input"
                    />
                </StepCard>
            )}

            {error && (
                <div
                    className="mt-6 rounded-lg bg-[#C84B31]/10 border border-[#C84B31]/30 p-4 text-[#C84B31] text-sm font-body"
                    data-testid="order-error"
                    role="alert"
                >
                    {error}
                </div>
            )}

            <div className="mt-10 flex items-center justify-between gap-4">
                <button
                    type="button"
                    onClick={goPrev}
                    disabled={step === 0 || submitting}
                    className="btn-outline disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="order-back-btn"
                >
                    ← Back
                </button>
                <button
                    type="button"
                    onClick={goNext}
                    disabled={submitting}
                    className="btn-primary min-w-[180px] justify-center"
                    data-testid="order-next-btn"
                >
                    {submitting
                        ? "Placing order…"
                        : step === ORDER_STEPS.length - 1
                        ? "Place Order"
                        : "Continue →"}
                </button>
            </div>
        </OrderShell>
    );
}

const StepCard = ({ title, hint, children }) => (
    <div>
        <h1 className="font-display text-3xl md:text-4xl font-black text-[#2C1A12] tracking-tighter leading-[1.1] mb-3">
            {title}
        </h1>
        {hint && (
            <p className="text-[#5C4D45] font-body mb-6">{hint}</p>
        )}
        <div className="mt-6">{children}</div>
    </div>
);

const OrderSuccess = ({ order, onReset }) => (
    <OrderShell>
        <div className="text-center mb-10">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#4A5D23]/15 flex items-center justify-center mb-6">
                <ShieldCheck className="w-10 h-10 text-[#4A5D23]" aria-hidden="true" />
            </div>
            <p className="overline mb-3">Confirmed</p>
            <h1 className="font-display text-4xl md:text-5xl font-black text-[#2C1A12] tracking-tighter">
                Order placed successfully!
            </h1>
            <p className="mt-4 text-[#5C4D45] font-body">
                The shop has received your order and will contact you if
                necessary.
            </p>
        </div>

        <div
            className="rounded-2xl bg-white border border-[#2C1A12]/10 p-8 md:p-10 shadow-[0_12px_40px_rgba(44,26,18,0.08)]"
            data-testid="order-success-summary"
        >
            <div className="flex items-baseline justify-between mb-6 pb-6 border-b border-[#2C1A12]/10">
                <div>
                    <p className="overline text-[#4A5D23] mb-2">Order ID</p>
                    <p
                        className="font-display text-3xl font-black text-[#2C1A12] tracking-tight"
                        data-testid="success-order-id"
                    >
                        {order.id}
                    </p>
                </div>
                <span className="text-xs font-semibold uppercase tracking-widest bg-[#C84B31]/10 text-[#C84B31] px-3 py-1.5 rounded-full">
                    {order.status}
                </span>
            </div>

            <dl className="space-y-4 font-body">
                <SummaryRow k="Quantity" v={`${order.quantity_kg} kg`} />
                <SummaryRow
                    k="Format"
                    v={order.coffee_format === "powder" ? "Coffee Powder" : "Roasted Coffee Beans"}
                />
                <SummaryRow k="Customer" v={order.customer_name} />
                <SummaryRow k="Delivery Address" v={order.address} />
                <SummaryRow k="Phone" v={order.phone} />
            </dl>
        </div>

        <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <button
                onClick={onReset}
                className="btn-primary"
                data-testid="success-home-btn"
            >
                Back to Home
            </button>
            <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline"
                data-testid="success-whatsapp-btn"
            >
                <MessageCircle className="w-4 h-4" aria-hidden="true" />
                Message on WhatsApp
            </a>
        </div>
    </OrderShell>
);

const SummaryRow = ({ k, v }) => (
    <div className="flex items-start justify-between gap-6">
        <dt className="text-xs uppercase tracking-widest text-[#5C4D45] font-semibold shrink-0 pt-1">
            {k}
        </dt>
        <dd className="text-[#2C1A12] font-medium text-right max-w-[70%]">{v}</dd>
    </div>
);


/* ============================== ADMIN AUTH =============================== */
const authAxios = axios.create({ baseURL: API, withCredentials: true });

const AdminAuthCtx = createContext({ admin: null, loading: true, login: null, logout: null });

const AdminAuthProvider = ({ children }) => {
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await authAxios.get("/auth/me");
                setAdmin(data);
            } catch {
                setAdmin(null);
            } finally {
                setLoading(false);
            }
        })();
        // authAxios is a module-level singleton; setState functions are stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await authAxios.post("/auth/login", { email, password });
        setAdmin(data);
        return data;
        // authAxios/setAdmin are stable references.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const logout = useCallback(async () => {
        try {
            await authAxios.post("/auth/logout");
        } catch (_err) {
            // Logout must always clear local state, even if the server call fails
            if (process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.debug("Logout API failed; clearing local state anyway:", _err?.message);
            }
        }
        setAdmin(null);
        // authAxios/setAdmin are stable references.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AdminAuthCtx.Provider value={{ admin, loading, login, logout }}>
            {children}
        </AdminAuthCtx.Provider>
    );
};

const useAdminAuth = () => useContext(AdminAuthCtx);


/* ============================== ADMIN LOGIN =============================== */
function AdminLogin() {
    const { admin, login } = useAdminAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (admin) navigate("/admin", { replace: true });
    }, [admin, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await login(email.trim(), password);
            navigate("/admin", { replace: true });
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#2C1A12] text-[#F5F2EA] flex items-center justify-center p-6">
            <form
                onSubmit={submit}
                className="w-full max-w-md bg-[#F5F2EA] text-[#2C1A12] rounded-2xl p-8 md:p-10 shadow-2xl"
                data-testid="admin-login-form"
            >
                <div className="flex items-center gap-2 mb-8">
                    <Coffee className="w-5 h-5 text-[#C84B31]" aria-hidden="true" />
                    <span className="font-display text-lg tracking-tight">
                        Vinayaka Coffee · Admin
                    </span>
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mb-2">
                    Shop Login
                </h1>
                <p className="text-[#5C4D45] font-body mb-8 text-sm">
                    Sign in to view and manage your customer orders.
                </p>

                <label className="block mb-5">
                    <span className="text-xs font-semibold uppercase tracking-widest text-[#5C4D45]">
                        Email
                    </span>
                    <input
                        type="email"
                        required
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 w-full rounded-lg border border-[#2C1A12]/20 bg-white px-4 py-3 focus:outline-none focus:border-[#C84B31]"
                        data-testid="admin-email-input"
                    />
                </label>
                <label className="block mb-6">
                    <span className="text-xs font-semibold uppercase tracking-widest text-[#5C4D45]">
                        Password
                    </span>
                    <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-2 w-full rounded-lg border border-[#2C1A12]/20 bg-white px-4 py-3 focus:outline-none focus:border-[#C84B31]"
                        data-testid="admin-password-input"
                    />
                </label>

                {error && (
                    <div
                        className="mb-6 rounded-lg bg-[#C84B31]/10 border border-[#C84B31]/30 p-3 text-[#C84B31] text-sm font-body"
                        data-testid="admin-login-error"
                        role="alert"
                    >
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={busy}
                    className="btn-primary w-full justify-center"
                    data-testid="admin-login-btn"
                >
                    {busy ? "Signing in…" : "Sign In"}
                </button>

                <Link
                    to="/"
                    className="mt-6 block text-center text-sm text-[#5C4D45] hover:text-[#C84B31]"
                    data-testid="admin-login-back"
                >
                    ← Back to storefront
                </Link>
            </form>
        </div>
    );
}


/* ============================== ADMIN DASHBOARD =========================== */
const STATUS_TONE = {
    "New": "bg-[#C84B31]/15 text-[#C84B31]",
    "Confirmed": "bg-[#4A5D23]/15 text-[#4A5D23]",
    "Preparing": "bg-amber-500/15 text-amber-700",
    "Out for Delivery": "bg-blue-500/15 text-blue-700",
    "Delivered": "bg-emerald-500/15 text-emerald-700",
    "Cancelled": "bg-neutral-400/20 text-neutral-600",
};
const ALL_STATUSES = [
    "New", "Confirmed", "Preparing", "Out for Delivery", "Delivered", "Cancelled",
];

function AdminDashboard() {
    const { admin, loading, logout } = useAdminAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState("");
    const [fetchErr, setFetchErr] = useState("");
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        if (!loading && !admin) navigate("/admin/login", { replace: true });
    }, [admin, loading, navigate]);

    useEffect(() => {
        if (!admin) return;
        let ok = true;
        (async () => {
            try {
                setFetchErr("");
                const q = filter ? `?status=${encodeURIComponent(filter)}` : "";
                const [ordersRes, statsRes] = await Promise.all([
                    authAxios.get(`/orders${q}`),
                    authAxios.get(`/orders/stats`),
                ]);
                if (!ok) return;
                setOrders(ordersRes.data);
                setStats(statsRes.data);
            } catch (_err) {
                if (!ok) return;
                setFetchErr("Could not load orders. Please refresh.");
                if (process.env.NODE_ENV !== "production") {
                    // eslint-disable-next-line no-console
                    console.error("Order fetch failed:", _err);
                }
            }
        })();
        return () => { ok = false; };
        // authAxios/encodeURIComponent/setState are all stable references or globals.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [admin, filter, refreshTick]);

    const changeStatus = async (id, status) => {
        try {
            await authAxios.patch(`/orders/${id}/status`, { status });
            setRefreshTick((t) => t + 1);
        } catch {
            setFetchErr("Could not update status. Please retry.");
        }
    };

    if (loading || !admin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F5F2EA]">
                <p className="text-[#5C4D45] font-body">Loading…</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F2EA]" data-testid="admin-dashboard">
            <header className="bg-[#2C1A12] text-[#F5F2EA]">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Coffee className="w-5 h-5 text-[#F5D6C8]" aria-hidden="true" />
                        <div>
                            <p className="font-display text-lg tracking-tight">
                                Vinayaka Coffee · Admin
                            </p>
                            <p className="text-xs text-[#F5F2EA]/60">
                                Signed in as {admin.email}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/"
                            className="text-sm text-[#F5F2EA]/70 hover:text-[#F5F2EA]"
                            data-testid="admin-storefront-link"
                        >
                            Storefront
                        </Link>
                        <button
                            onClick={async () => { await logout(); navigate("/admin/login"); }}
                            className="text-sm bg-[#F5F2EA]/10 hover:bg-[#F5F2EA]/20 px-4 py-2 rounded-full"
                            data-testid="admin-logout-btn"
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-10">
                <div className="mb-8 flex flex-wrap items-baseline justify-between gap-4">
                    <div>
                        <p className="overline mb-2">Live Orders</p>
                        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
                            {stats?.total || 0} total orders
                        </h1>
                    </div>
                    <button
                        onClick={() => setRefreshTick((t) => t + 1)}
                        className="btn-outline text-sm"
                        data-testid="admin-refresh-btn"
                    >
                        Refresh
                    </button>
                </div>

                {/* Status filter chips */}
                <div className="flex flex-wrap gap-2 mb-8">
                    <FilterChip label="All" active={!filter} count={stats?.total || 0} onClick={() => setFilter("")} testId="filter-all" />
                    {ALL_STATUSES.map((s) => (
                        <FilterChip
                            key={s}
                            label={s}
                            active={filter === s}
                            count={stats?.by_status?.[s] || 0}
                            onClick={() => setFilter(s)}
                            testId={`filter-${s.toLowerCase().replace(/\s/g, "-")}`}
                        />
                    ))}
                </div>

                {fetchErr && (
                    <div className="mb-6 rounded-lg bg-[#C84B31]/10 border border-[#C84B31]/30 p-4 text-[#C84B31] text-sm">
                        {fetchErr}
                    </div>
                )}

                {orders.length === 0 ? (
                    <div className="rounded-2xl bg-white border border-[#2C1A12]/10 p-12 text-center text-[#5C4D45] font-body">
                        No orders {filter ? `with status "${filter}"` : "yet"}.
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="admin-orders-list">
                        {orders.map((o) => (
                            <OrderRow key={o.id} order={o} onChange={changeStatus} />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

const FilterChip = ({ label, active, count, onClick, testId }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 rounded-full text-sm font-semibold font-body transition-colors ${
            active
                ? "bg-[#2C1A12] text-[#F5F2EA]"
                : "bg-white text-[#2C1A12] border border-[#2C1A12]/15 hover:border-[#2C1A12]/40"
        }`}
        data-testid={testId}
    >
        {label}
        <span className={`ml-2 text-xs ${active ? "text-[#F5D6C8]" : "text-[#5C4D45]"}`}>
            {count}
        </span>
    </button>
);

const OrderRow = ({ order, onChange }) => {
    const created = new Date(order.created_at);
    return (
        <article
            className="bg-white border border-[#2C1A12]/10 rounded-2xl p-6 md:p-8 grid md:grid-cols-12 gap-6"
            data-testid={`order-row-${order.id}`}
        >
            {/* Left: ID + meta */}
            <div className="md:col-span-3">
                <p className="overline mb-2">Order ID</p>
                <p className="font-display text-xl font-bold tracking-tight">
                    {order.id}
                </p>
                <p className="mt-2 text-xs text-[#5C4D45] font-body">
                    {created.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </p>
                <span
                    className={`inline-block mt-4 text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full ${STATUS_TONE[order.status]}`}
                >
                    {order.status}
                </span>
            </div>

            {/* Middle: order + customer */}
            <div className="md:col-span-6 space-y-3 font-body text-sm">
                <div>
                    <span className="text-xs uppercase tracking-widest text-[#5C4D45] font-semibold">Quantity & Type · </span>
                    <span className="text-[#2C1A12] font-semibold">
                        {order.quantity_kg} kg · {order.coffee_format === "powder" ? "Coffee Powder" : "Roasted Beans"}
                    </span>
                </div>
                <div>
                    <span className="text-xs uppercase tracking-widest text-[#5C4D45] font-semibold">Customer · </span>
                    <span className="text-[#2C1A12] font-semibold">{order.customer_name}</span>
                </div>
                <div>
                    <span className="text-xs uppercase tracking-widest text-[#5C4D45] font-semibold">Phone · </span>
                    <a
                        href={`tel:${order.phone}`}
                        className="text-[#C84B31] font-semibold hover:underline"
                    >
                        {order.phone}
                    </a>
                    <a
                        href={`https://wa.me/${order.phone.replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-3 text-xs text-[#4A5D23] hover:underline"
                    >
                        WhatsApp
                    </a>
                </div>
                <div>
                    <span className="text-xs uppercase tracking-widest text-[#5C4D45] font-semibold">Deliver to · </span>
                    <span className="text-[#2C1A12]">{order.address}</span>
                </div>
            </div>

            {/* Right: status selector */}
            <div className="md:col-span-3">
                <p className="overline mb-2">Change Status</p>
                <select
                    value={order.status}
                    onChange={(e) => onChange(order.id, e.target.value)}
                    className="w-full bg-[#F5F2EA] border border-[#2C1A12]/15 rounded-lg px-3 py-2 text-sm font-semibold text-[#2C1A12] focus:outline-none focus:border-[#C84B31]"
                    data-testid={`order-status-select-${order.id}`}
                >
                    {ALL_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>
        </article>
    );
};


/* ============================ TAP LANDING (IG/FB) ======================== */
function TapLanding() {
    const waLink = `https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent(
        "Hi Vinayaka Coffee Yercaud, I would like to order Pure Filter Coffee.",
    )}`;
    return (
        <div
            className="min-h-screen flex flex-col relative overflow-hidden"
            style={{
                background:
                    "linear-gradient(180deg, #E7F5EA 0%, #FFFFFF 45%, #FFFFFF 100%)",
            }}
            data-testid="tap-landing"
        >
            {/* Soft WhatsApp-green top strip like a chat header */}
            <div className="bg-[#075E54] text-white pt-6 pb-4 px-6 shadow-md">
                <div className="max-w-md mx-auto flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden shrink-0">
                        <Coffee className="w-6 h-6 text-[#075E54]" aria-hidden="true" />
                    </div>
                    <div>
                        <p className="font-semibold text-[15px] tracking-tight leading-tight">
                            Vinayaka Coffee Yercaud
                        </p>
                        <p className="text-[11px] text-white/70 leading-tight mt-0.5 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] inline-block" />
                            online · replies within minutes
                        </p>
                    </div>
                </div>
            </div>

            <main className="flex-1 px-6 py-10 flex flex-col justify-between max-w-md w-full mx-auto">
                {/* Chat-style bubble intro */}
                <div>
                    <div className="mb-8 flex justify-start">
                        <div
                            className="max-w-[85%] bg-white rounded-2xl rounded-tl-sm px-5 py-4 shadow-[0_6px_20px_rgba(7,94,84,0.08)] border border-[#E7F5EA]"
                            style={{ fontFamily: "'Manrope', sans-serif" }}
                        >
                            <p className="text-[11px] uppercase tracking-widest text-[#4A5D23] font-semibold mb-2">
                                Vinayaka Coffee
                            </p>
                            <h1 className="text-[22px] leading-[1.25] text-[#2C1A12] font-bold">
                                Authentic Yercaud Filter Coffee delivered to
                                your doorstep.
                            </h1>
                            <p className="mt-3 text-[15px] text-[#5C4D45]">
                                Tap below to order&nbsp;👇
                            </p>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="space-y-4">
                        <a
                            href={waLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-3 w-full py-5 rounded-2xl bg-[#25D366] text-white text-lg font-bold shadow-[0_10px_28px_rgba(37,211,102,0.35)] active:scale-[0.98] transition-transform"
                            data-testid="tap-whatsapp-btn"
                            style={{ fontFamily: "'Manrope', sans-serif" }}
                        >
                            <MessageCircle
                                className="w-6 h-6 fill-white"
                                aria-hidden="true"
                            />
                            ORDER ON WHATSAPP
                        </a>

                        <a
                            href="https://www.vinayakacoffeeyercaud.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-3 w-full py-5 rounded-2xl bg-[#2C1A12] text-white text-lg font-bold shadow-[0_10px_28px_rgba(44,26,18,0.3)] active:scale-[0.98] transition-transform"
                            data-testid="tap-website-btn"
                            style={{ fontFamily: "'Manrope', sans-serif" }}
                        >
                            <Coffee className="w-6 h-6" aria-hidden="true" />
                            ORDER ON WEBSITE
                        </a>
                    </div>

                    {/* Trust row */}
                    <div className="mt-8 grid grid-cols-3 gap-2 text-center">
                        {[
                            { icon: Sparkles, label: "Freshly Roasted" },
                            { icon: Truck, label: "Pan-India Ship" },
                            { icon: ShieldCheck, label: "100% Pure" },
                        ].map((t) => (
                            <div
                                key={t.label}
                                className="rounded-xl bg-white border border-[#E7F5EA] py-3 px-2"
                            >
                                <t.icon
                                    className="w-4 h-4 text-[#075E54] mx-auto mb-1.5"
                                    aria-hidden="true"
                                />
                                <p className="text-[11px] font-semibold text-[#2C1A12] leading-tight">
                                    {t.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Final CTA */}
                <div className="pt-10 pb-4 text-center">
                    <p
                        className="text-[17px] font-bold text-[#075E54]"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                        Order your Pure Filter Coffee today ☕
                    </p>
                    <p className="mt-3 text-[11px] text-[#5C4D45]/70 font-body">
                        © Vinayaka Coffee Yercaud · Tamil Nadu, India
                    </p>
                </div>
            </main>
        </div>
    );
}


/* ================================= APP =================================== */
function App() {
    return (
        <AdminAuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Marketing />} />
                    <Route path="/order" element={<OrderFlow />} />
                    <Route path="/tap" element={<TapLanding />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AdminAuthProvider>
    );
}

export default App;
